let _ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    if (!_ctx) {
      const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return null;
      _ctx = new Ctor();
    }
    return _ctx;
  } catch {
    return null;
  }
}

function playTone(freq: number, duration = 0.12, type: OscillatorType = "sine", gain = 0.05) {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  g.gain.setValueAtTime(0, ctx.currentTime);
  g.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + duration);
}

export function playTickSound() {
  playTone(880, 0.06, "triangle", 0.04);
}

export function playGoalSound() {
  // Pleasant 3-note arpeggio
  playTone(523, 0.12, "sine", 0.05);
  setTimeout(() => playTone(659, 0.12, "sine", 0.05), 90);
  setTimeout(() => playTone(784, 0.18, "sine", 0.05), 180);
}

export function playWinSound() {
  // Triumphant chord
  playTone(523, 0.25, "sine", 0.04);
  playTone(659, 0.25, "sine", 0.04);
  playTone(784, 0.3, "sine", 0.04);
  setTimeout(() => playTone(1047, 0.35, "sine", 0.05), 120);
}

export function playBigWinSound() {
  // Extended fanfare for monthly milestones: rising arpeggio + sustained chord.
  playTone(523, 0.16, "sine", 0.04);
  setTimeout(() => playTone(659, 0.16, "sine", 0.04), 90);
  setTimeout(() => playTone(784, 0.16, "sine", 0.04), 180);
  setTimeout(() => playTone(1047, 0.22, "sine", 0.05), 270);
  setTimeout(() => {
    playTone(523, 0.5, "sine", 0.035);
    playTone(659, 0.5, "sine", 0.035);
    playTone(784, 0.5, "sine", 0.035);
    playTone(1047, 0.55, "sine", 0.045);
    playTone(1319, 0.55, "sine", 0.04);
  }, 420);
}

export type ConfettiIntensity = "normal" | "large" | "huge";

const CONFETTI_PRESETS: Record<
  ConfettiIntensity,
  { count: number; duration: number; spread: number; sizeBoost: number; speedBoost: number }
> = {
  normal: { count: 90, duration: 1800, spread: 80, sizeBoost: 0, speedBoost: 0 },
  large: { count: 150, duration: 2600, spread: 140, sizeBoost: 2, speedBoost: 2 },
  huge: { count: 220, duration: 3600, spread: 200, sizeBoost: 3, speedBoost: 4 },
};

export function fireConfetti(opts: { intensity?: ConfettiIntensity } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const preset = CONFETTI_PRESETS[opts.intensity ?? "normal"];
  const canvas = document.createElement("canvas");
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.width = "100vw";
  canvas.style.height = "100vh";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "9999";
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    document.body.removeChild(canvas);
    return;
  }
  const colors = ["#22c55e", "#a855f7", "#3b82f6", "#f59e0b", "#ec4899"];
  type Piece = { x: number; y: number; vx: number; vy: number; rot: number; vr: number; size: number; color: string };
  const pieces: Piece[] = [];
  for (let i = 0; i < preset.count; i += 1) {
    pieces.push({
      x: window.innerWidth / 2 + (Math.random() - 0.5) * preset.spread,
      y: window.innerHeight / 2,
      vx: (Math.random() - 0.5) * (12 + preset.speedBoost),
      vy: -Math.random() * (14 + preset.speedBoost) - 4,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.3,
      size: 6 + Math.random() * 6 + preset.sizeBoost,
      color: colors[i % colors.length],
    });
  }
  const start = performance.now();
  const animate = (t: number) => {
    const elapsed = t - start;
    if (elapsed > preset.duration) {
      document.body.removeChild(canvas);
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const p of pieces) {
      p.vy += 0.4;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
    requestAnimationFrame(animate);
  };
  requestAnimationFrame(animate);
}
